/**
 * Recommendation engine for stone matching
 * Scores stones based on user profile and returns ranked recommendations
 */

/**
 * @typedef {Object} Profile
 * @property {string[]} goals - User's primary needs/goals
 * @property {string} [protectionMode] - Type of protection preferred
 * @property {string} [sensitivity] - User's sensitivity level
 * @property {string} [intensityPref] - Preferred energy intensity
 * @property {string} [usage] - How the user plans to use the stone
 * @property {string} [chakra] - Specific chakra to focus on
 */

/**
 * @typedef {Object} ScoredStone
 * @property {import('../data/stones').Stone} stone - The stone object
 * @property {number} score - Calculated score
 * @property {string[]} reasons - Reasons for the score
 */

/**
 * @typedef {Object} RecommendationResult
 * @property {ScoredStone[]} top - Top 3 recommended stones
 * @property {ScoredStone[]} alternatives - 2 alternative stones
 * @property {ScoredStone[]} candidates - All top 8 candidates
 */

/**
 * Score a single stone against a user profile
 * @param {import('../data/stones').Stone} stone 
 * @param {Profile} profile 
 * @returns {ScoredStone}
 */
export const scoreStone = (stone, profile) => {
  let score = 0;
  const reasons = [];

  // +3 for each matching need/goal
  if (profile.goals && profile.goals.length > 0) {
    const matchingNeeds = stone.needs.filter(need => 
      profile.goals.includes(need)
    );
    if (matchingNeeds.length > 0) {
      score += matchingNeeds.length * 3;
      matchingNeeds.forEach(need => {
        const needLabels = {
          stress: 'Apaise le stress',
          sommeil: 'Favorise le sommeil',
          protection: 'Offre une protection',
          confiance: 'Renforce la confiance',
          ancrage: 'Aide à l\'ancrage',
          concentration: 'Améliore la concentration',
          communication: 'Facilite la communication',
          intuition: 'Développe l\'intuition',
          harmonie: 'Apporte l\'harmonie',
          energie_positive: 'Attire l\'énergie positive'
        };
        reasons.push(needLabels[need] || need);
      });
    }
  }

  // +2 if protection mode matches family
  if (profile.protectionMode && stone.families.includes(profile.protectionMode)) {
    score += 2;
    const familyLabels = {
      absorbante: 'Pierre absorbante qui neutralise les énergies négatives',
      répulsive: 'Pierre répulsive qui renvoie les énergies négatives',
      apaisante: 'Pierre apaisante qui harmonise les énergies',
      rayonnante: 'Pierre rayonnante qui diffuse une protection positive'
    };
    reasons.push(familyLabels[profile.protectionMode] || `Protection ${profile.protectionMode}`);
  }

  // +2 if intensity preference matches
  if (profile.intensityPref && stone.intensity === profile.intensityPref) {
    score += 2;
    const intensityLabels = {
      douce: 'Énergie douce et subtile',
      moyenne: 'Énergie équilibrée',
      forte: 'Énergie puissante'
    };
    reasons.push(intensityLabels[profile.intensityPref] || `Intensité ${profile.intensityPref}`);
  }

  // +2 if sensitivity fits
  if (profile.sensitivity && stone.sensitivityFit.includes(profile.sensitivity)) {
    score += 2;
    reasons.push('Adaptée à votre sensibilité');
  }

  // +1 if usage fits
  if (profile.usage && stone.usageFits.includes(profile.usage)) {
    score += 1;
    const usageLabels = {
      sur_soi: 'Parfaite à porter sur soi',
      maison: 'Idéale pour la maison',
      meditation: 'Excellente pour la méditation',
      table_de_nuit: 'Recommandée pour la table de nuit'
    };
    reasons.push(usageLabels[profile.usage] || `Usage: ${profile.usage}`);
  }

  // +1 if chakra matches (partial text match)
  if (profile.chakra && profile.chakra !== 'unknown') {
    const chakraMapping = {
      racine: 'racine',
      sacre: 'sacré',
      plexus: 'plexus',
      coeur: 'cœur',
      gorge: 'gorge',
      troisieme_oeil: 'troisième œil',
      couronne: 'couronne'
    };
    const chakraSearch = chakraMapping[profile.chakra] || profile.chakra;
    const hasChakra = stone.chakras.some(c => 
      c.toLowerCase().includes(chakraSearch.toLowerCase())
    );
    if (hasChakra) {
      score += 1;
      reasons.push(`Associée au chakra ${chakraSearch}`);
    }
  }

  // -3 penalty if very sensitive and stone is intense
  if (profile.sensitivity === 'tres_sensible' && stone.intensity === 'forte') {
    score -= 3;
    reasons.push('⚠️ Énergie potentiellement trop intense pour les personnes sensibles');
  }

  // Bonus for matching energy style with goals
  if (profile.goals) {
    if (profile.goals.includes('stress') && stone.energyStyle.includes('calme')) {
      score += 1;
    }
    if (profile.goals.includes('energie_positive') && stone.energyStyle.includes('dynamique')) {
      score += 1;
    }
    if (profile.goals.includes('protection') && stone.energyStyle.includes('protectrice')) {
      score += 1;
    }
  }

  return {
    stone,
    score,
    reasons: reasons.filter((r, i, arr) => arr.indexOf(r) === i) // Remove duplicates
  };
};

/**
 * Get recommendations based on user profile
 * @param {import('../data/stones').Stone[]} stones 
 * @param {Profile} profile 
 * @returns {RecommendationResult}
 */
export const recommend = (stones, profile) => {
  // Score all stones
  const scoredStones = stones.map(stone => scoreStone(stone, profile));
  
  // Sort by score (descending)
  scoredStones.sort((a, b) => b.score - a.score);
  
  // Get top 8 candidates
  const candidates = scoredStones.slice(0, 8);
  
  // Split into top 3 and alternatives (next 2)
  const top = candidates.slice(0, 3);
  const alternatives = candidates.slice(3, 5);

  return {
    top,
    alternatives,
    candidates
  };
};

/**
 * Get a quick preview of top candidates (used during quiz)
 * @param {import('../data/stones').Stone[]} stones 
 * @param {Profile} profile 
 * @returns {ScoredStone[]}
 */
export const getTopCandidates = (stones, profile) => {
  const scoredStones = stones.map(stone => scoreStone(stone, profile));
  scoredStones.sort((a, b) => b.score - a.score);
  return scoredStones.slice(0, 8);
};

export default { scoreStone, recommend, getTopCandidates };
